// Черновите на складовите документи на сървъра — листовете на НОВ документ:
// доставка, изписване/бракуване, прехвърляне, инвентаризация. Една незавършена
// на потребител и тип, видима за колегите, със заключване — който я държи
// отворена, подновява заключването с heartbeat (изтича след 2 мин.); колега
// може да я „поеме". Autosave-ът не се одитира, триенето и поемането — да.
// Договорът е в docs/cross-repo-contract.md (литералите на `DraftType` са и в
// BE `router_drafts.DraftType`).
import { api } from "./client";

export type DraftType = "delivery" | "writeoff" | "transfer" | "stocktake";
export type DraftStatus = "draft" | "held";

export interface InventoryDraft {
  id: number;
  type: DraftType;
  ownerUserId: number;
  ownerName: string | null;
  // ЕТИКЕТЪТ на черновата в списъка: при доставка — доставчикът; при другите —
  // складът / посоката / причината (сглобява ги листът при запис)
  supplierName: string | null;
  // видът за хапчето: „invoice"/„note" при доставка, „writeoff"/„scrap" при
  // изписване, иначе null
  documentType: string | null;
  linesCount: number;
  totalWithVat: number | null;
  status: DraftStatus;
  problemText: string | null;
  lockedBy: number | null;
  lockedByName: string | null;
  lockedAt: string | null;
  // някой ДРУГ я държи отворена в последните 2 минути
  lockActive: boolean;
  // кога е започната (BE отпреди полето го няма)
  createdAt?: string | null;
  updatedAt: string | null;
  // складът на листа — копие от `payload.warehouseId` (инвентаризация,
  // изписване); стар BE го няма → свързване по етикета
  warehouseId?: number | null;
  mine: boolean;
}

export interface InventoryDraftDetail<P = unknown> extends InventoryDraft {
  payload: P;
}

export interface DraftBody<P = unknown> {
  payload: P;
  supplierName: string | null;
  documentType: string | null;
  linesCount: number;
  totalWithVat: number | null;
  status: DraftStatus;
  problemText: string | null;
}

export async function listDrafts(type: DraftType): Promise<InventoryDraft[]> {
  const res = await api.get<InventoryDraft[]>("/inventory/drafts", { params: { type } });
  return res.data;
}

export async function getDraft<P>(id: number): Promise<InventoryDraftDetail<P>> {
  const res = await api.get<InventoryDraftDetail<P>>(`/inventory/drafts/${id}`);
  return res.data;
}

// Моята чернова (upsert — една на потребител и тип)
export async function saveMyDraft<P>(type: DraftType, body: DraftBody<P>): Promise<InventoryDraft> {
  const res = await api.put<InventoryDraft>("/inventory/drafts/mine", body, { params: { type } });
  return res.data;
}

// Чужда чернова — само докато държа заключването (иначе 409)
export async function saveDraft<P>(id: number, body: DraftBody<P>): Promise<InventoryDraft> {
  const res = await api.put<InventoryDraft>(`/inventory/drafts/${id}`, body);
  return res.data;
}

export async function heartbeatDraft(id: number): Promise<InventoryDraft> {
  const res = await api.post<InventoryDraft>(`/inventory/drafts/${id}/heartbeat`);
  return res.data;
}

export async function takeOverDraft(id: number): Promise<InventoryDraft> {
  const res = await api.post<InventoryDraft>(`/inventory/drafts/${id}/take-over`);
  return res.data;
}

export async function releaseDraft(id: number): Promise<void> {
  await api.post(`/inventory/drafts/${id}/release`);
}

export async function deleteDraft(id: number): Promise<void> {
  await api.delete(`/inventory/drafts/${id}`);
}

// 409 от заключването: кой държи черновата
export const lockHolderOf = (error: unknown): string | null => {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "lockedByName" in detail)
    return String((detail as { lockedByName?: unknown }).lockedByName ?? "");
  return "";
};
